import { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { AuthContext } from "../../../contexts/AuthContext";
import { tempUrl } from "../../../contexts/ContextProvider";
import { Loader } from "../../../components";
import { Alert, Button, Card, Col, Container, Form, Row, Table } from "react-bootstrap";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import SaveIcon from "@mui/icons-material/Save";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

const toIsoDate = (value) => {
  if (!value) return "";
  const text = String(value);
  const indonesia = text.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (indonesia) return indonesia[3] + "-" + indonesia[2] + "-" + indonesia[1];
  return text.slice(0, 10);
};

const toDateObject = (value) => {
  const isoDate = toIsoDate(value);
  if (!isoDate) return null;
  const parts = isoDate.split("-").map(Number);
  if (parts.length !== 3 || parts.some((part) => Number.isNaN(part))) return null;
  const date = new Date(parts[0], parts[1] - 1, parts[2]);
  return Number.isNaN(date.getTime()) ? null : date;
};

const dateToIso = (date) => {
  if (!date) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return year + "-" + month + "-" + day;
};

const formatNumber = (value) => (Number(value) || 0).toLocaleString("id-ID", {
  maximumFractionDigits: 6,
});

const stockLabel = (stock) => {
  if (!stock) return "";
  const value = stock.stok || stock;
  return value.kodeStok + " - " + value.namaStok;
};

const EntryRacikanForm = ({ editId }) => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [tanggal, setTanggal] = useState(new Date().toISOString().slice(0, 10));
  const [coa, setCoa] = useState(null);
  const [coas, setCoas] = useState([]);
  const [stockOptions, setStockOptions] = useState([]);
  const [children, setChildren] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const coaOptions = useMemo(() => coas, [coas]);

  useEffect(() => {
    loadOptions();
    if (editId) loadEntry();
  }, [editId]);

  const loadOptions = async () => {
    setLoading(true);
    try {
      const [coaResponse, stockResponse] = await Promise.all([
        axios.post(tempUrl + "/COAs", { _id: user.id, token: user.token }),
        axios.post(tempUrl + "/entryRacikanStokOptions", { _id: user.id, token: user.token }),
      ]);
      setCoas(coaResponse.data || []);
      setStockOptions(stockResponse.data || []);
    } catch (requestError) {
      setError(requestError.response?.data?.message || requestError.message);
    }
    setLoading(false);
  };

  const loadEntry = async () => {
    try {
      const response = await axios.post(tempUrl + "/entryRacikans/" + editId, {
        _id: user.id,
        token: user.token,
      });
      const data = response.data;
      setTanggal(toIsoDate(data.tanggal));
      setCoa(data.coa || null);
      setChildren((data.children || []).map((child) => ({
        id: child.id,
        stokId: child.stokId,
        stok: child.stok,
        quantity: child.quantity,
        items: (child.items || []).map((item) => ({
          id: item.id,
          stokId: item.stokId,
          stok: item.stok,
          quantity: item.quantity,
          quantityTotal: item.quantityTotal ?? ((Number(child.quantity) || 0) * (Number(item.quantity) || 0)),
          bomQuantity: item.quantity,
        })),
      })));
    } catch (requestError) {
      setError(requestError.response?.data?.message || requestError.message);
    }
  };

  const loadItems = async (stokId) => {
    const response = await axios.post(tempUrl + "/entryRacikanStokItems", {
      stokId,
      _id: user.id,
      token: user.token,
    });
    return (response.data || []).map((item) => ({
      stokId: item.stokId,
      stok: item.stok,
      quantity: item.quantity,
      quantityTotal: "",
      bomQuantity: item.quantity,
    }));
  };

  const updateChild = (index, changes) => {
    setChildren((current) => current.map((child, childIndex) => {
      if (childIndex !== index) return child;

      const nextChild = { ...child, ...changes };
      if (Object.prototype.hasOwnProperty.call(changes, "quantity")) {
        const childQuantity = Number(changes.quantity) || 0;
        nextChild.items = (child.items || []).map((item) => ({
          ...item,
          quantityTotal: childQuantity * (Number(item.quantity) || 0),
        }));
      }
      return nextChild;
    }));
  };

  const handleStockChange = async (index, value) => {
    if (!value) {
      updateChild(index, { stokId: "", stok: null, items: [] });
      return;
    }
    try {
      setError("");
      const items = await loadItems(value.stokId || value.stok?.id);
      const childQuantity = children[index]?.quantity;
      const preparedItems = items.map((item) => ({
        ...item,
        quantityTotal: (Number(childQuantity) || 0) * (Number(item.bomQuantity) || 0),
      }));
      updateChild(index, {
        stokId: value.stokId || value.stok?.id,
        stok: value.stok || value,
        items: preparedItems,
      });
    } catch (requestError) {
      setError(requestError.response?.data?.message || requestError.message);
    }
  };

  const updateItemTotal = (childIndex, itemIndex, quantityTotal) => {
    setChildren((current) => current.map((child, index) => {
      if (index !== childIndex) return child;
      return {
        ...child,
        items: child.items.map((item, itemIndexCurrent) =>
          itemIndexCurrent === itemIndex
            ? {
              ...item,
              quantityTotal,
            }
            : item,
        ),
      };
    }));
  };

  const save = async (event) => {
    event.preventDefault();
    setError("");
    const selectedChildren = children.filter((child) => child.stokId);
    if (!tanggal || selectedChildren.length === 0) {
      setError("Tanggal dan minimal satu stok racikan wajib diisi.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        tanggal,
        coaId: coa?.id || null,
        kodeCabang: user.cabang.id,
        userIdInput: user.id,
        userIdUpdate: user.id,
        _id: user.id,
        token: user.token,
        listEntryRacikanChild: selectedChildren.map((child) => ({
          stokId: child.stokId,
          quantity: child.quantity === "" ? 0 : child.quantity,
          items: (child.items || []).map((item) => ({
            stokId: item.stokId,
            quantity: item.quantity === "" ? 0 : item.quantity,
            quantityTotal: item.quantityTotal === "" ? 0 : item.quantityTotal,
          })),
        })),
      };
      if (editId) {
        await axios.post(tempUrl + "/updateEntryRacikan/" + editId, payload);
      } else {
        await axios.post(tempUrl + "/saveEntryRacikan", payload);
      }
      navigate("/daftarEntryRacikan");
    } catch (requestError) {
      setError(requestError.response?.data?.message || requestError.message);
    }
    setSaving(false);
  };

  if (loading) return <Loader />;

  return (
    <Container fluid className="py-3 px-2 px-md-4">
      <div className="text-secondary small">Transaksi</div>
      <h3 className="fw-bold mb-1">{editId ? "Ubah Entry Racikan" : "Tambah Entry Racikan"}</h3>
      <p className="text-muted mb-3">Simpan snapshot BOM sesuai kebutuhan transaksi.</p>
      {error && <Alert variant="danger" dismissible onClose={() => setError("")}>{error}</Alert>}

      <Card className="border-0 shadow-sm mb-3">
        <Card.Header className="bg-primary text-white fw-semibold py-2">Data Entry Racikan</Card.Header>
        <Card.Body className="p-3">
          <Row className="g-3">
            <Col xs={12} md={4} lg={3}>
              <Form.Group>
                <Form.Label className="small fw-semibold mb-1">Tanggal <span className="text-danger">*</span></Form.Label>
                <DatePicker
                  selected={toDateObject(tanggal)}
                  onChange={(date) => setTanggal(dateToIso(date))}
                  dateFormat="dd/MM/yyyy"
                  placeholderText="DD/MM/YYYY"
                  customInput={<Form.Control size="sm" required />}
                  wrapperClassName="w-100"
                />
              </Form.Group>
            </Col>
            <Col xs={12} md={8} lg={6}>
              <Form.Group>
                <Form.Label className="small fw-semibold mb-1">COA</Form.Label>
                <Autocomplete
                  size="small"
                  options={coaOptions}
                  value={coa}
                  onChange={(_, value) => setCoa(value)}
                  isOptionEqualToValue={(option, value) => Number(option.id) === Number(value.id)}
                  getOptionLabel={(option) => option ? option.kodeCOA + " - " + option.namaCOA : ""}
                  filterOptions={(options, state) => {
                    const keyword = state.inputValue.toUpperCase();
                    return options.filter((option) =>
                      (option.kodeCOA + " " + option.namaCOA).toUpperCase().includes(keyword),
                    );
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Ketik kode atau nama COA"
                      size="small"
                      sx={{
                        "& .MuiInputBase-root": { minHeight: 31, fontSize: "0.875rem" },
                        "& .MuiOutlinedInput-input": { padding: "5px 8px" },
                      }}
                    />
                  )}
                />
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Card className="border-0 shadow-sm">
        <Card.Header className="bg-primary text-white d-flex justify-content-between align-items-center py-2">
          <span className="fw-semibold">Detail Entry Racikan</span>
          <Button size="sm" variant="light" onClick={() => setChildren((current) => [...current, { stokId: "", quantity: "", items: [] }])}>
            + Input Racikan
          </Button>
        </Card.Header>
        <Card.Body className="p-2 p-md-3">
          {children.length === 0 && <div className="text-center text-muted small py-4">Belum ada stok racikan. Klik tombol Input Racikan.</div>}
          {children.map((child, childIndex) => {
            const options = stockOptions.some((option) => Number(option.stokId) === Number(child.stokId)) || !child.stok
              ? stockOptions
              : [{ stokId: child.stokId, stok: child.stok }, ...stockOptions];
            const selectedStock = options.find((option) => Number(option.stokId) === Number(child.stokId)) || null;
            return (
              <Card key={child.id || childIndex} className="border mb-3">
                <Card.Header className="bg-light py-2 d-flex justify-content-between align-items-center">
                  <span className="small fw-semibold">Racikan #{childIndex + 1}</span>
                  <Button size="sm" variant="outline-danger" title="Hapus racikan" onClick={() => setChildren((current) => current.filter((_, index) => index !== childIndex))}>
                    <DeleteOutlineIcon fontSize="small" />
                  </Button>
                </Card.Header>
                <Card.Body className="p-2 p-md-3">
                  <Row className="g-2 align-items-end">
                    <Col xs={12} md={8}>
                      <Form.Group>
                        <Form.Label className="small mb-1">Stok Racikan (Jenis 02) <span className="text-danger">*</span></Form.Label>
                        <Autocomplete
                          size="small"
                          options={options}
                          value={selectedStock}
                          onChange={(_, value) => handleStockChange(childIndex, value)}
                          isOptionEqualToValue={(option, value) => Number(option.stokId) === Number(value.stokId)}
                          getOptionLabel={stockLabel}
                          filterOptions={(stockList, state) => {
                            const keyword = state.inputValue.toUpperCase();
                            return stockList.filter((option) => stockLabel(option).toUpperCase().includes(keyword));
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              placeholder="Ketik kode atau nama stok"
                              size="small"
                              required
                              sx={{
                                "& .MuiInputBase-root": { minHeight: 31, fontSize: "0.875rem" },
                                "& .MuiOutlinedInput-input": { padding: "5px 8px" },
                              }}
                            />
                          )}
                        />
                      </Form.Group>
                    </Col>
                    <Col xs={12} md={3}>
                      <Form.Group>
                        <Form.Label className="small mb-1">Quantity</Form.Label>
                        <Form.Control size="sm" type="number" min="0" step="any" value={child.quantity} onChange={(event) => updateChild(childIndex, { quantity: event.target.value })} />
                      </Form.Group>
                    </Col>
                  </Row>

                  {child.items?.length > 0 && (
                    <div className="mt-3 ms-md-3 ps-md-3 border-start border-primary">
                      <div className="small fw-semibold text-primary mb-2">Item BOM (Snapshot)</div>
                      <Table responsive size="sm" bordered hover className="align-middle mb-0">
                        <thead className="table-light">
                          <tr><th>Stok Item</th><th style={{ width: 170 }}>Quantity / Unit</th><th style={{ width: 170 }}>Quantity Total</th></tr>
                        </thead>
                        <tbody>
                          {child.items.map((item, itemIndex) => (
                            <tr key={item.id || itemIndex}>
                              <td className="small">{stockLabel(item.stok)}</td>
                              <td><Form.Control size="sm" type="number" min="0" step="any" value={item.quantity} disabled /></td>
                              <td><Form.Control size="sm" type="number" min="0" step="any" value={item.quantityTotal} onChange={(event) => updateItemTotal(childIndex, itemIndex, event.target.value)} /></td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  )}
                </Card.Body>
              </Card>
            );
          })}
        </Card.Body>
      </Card>
      <div className="d-flex gap-2 mt-3">
        <Button variant="outline-secondary" size="sm" onClick={() => navigate("/daftarEntryRacikan")}>Kembali</Button>
        <Button type="button" variant="primary" size="sm" disabled={saving} onClick={save}>
          <SaveIcon fontSize="small" className="me-1" /> {saving ? "Menyimpan..." : editId ? "Edit" : "Simpan"}
        </Button>
      </div>
    </Container>
  );
};

export default EntryRacikanForm;
